// src/systems/save.ts
//
// Phase 4: localStorage persistence with schema-versioned discard policy.
// Phase 12 (12b — SAVE-06/SAVE-07): SAVE_KEY renamed v1 → v2; schema bumped
// to v:3 (queuedBall field added in this phase). v:1 and v:2 payloads are
// silently discarded on load per the locked discard-on-mismatch policy.
// Phase 15 (15-05 — SAVE-06): schema bumped to v:4 (consumables, forcedNumber,
// pendingInterestBonus fields). v:3 and below are silently discarded on load.
// Phase 16 (16-01): schema bumped to v:5 (lastSpin, currentStreak,
// standardFichePurchasesThisVisit fields). v:4 and below silently discarded.
// Phase 19 (19-01): schema bumped to v:6 (starting consumables in run-init).
// v:5 and below silently discarded.
// Phase 21 (21-03): schema bumped to v:7 (boardLayout now contains outside-sub
// cells instead of monolithic outside cells). v:6 and below silently discarded.
// Phase 22 (row-swap): schema bumped to v:9 (dozen-sub cells moved to row 0,
// outside-sub cells moved to row 4). v:7 and below silently discarded.
// Phase 23 (current): bumped to v:11 (pawnProgress + tetrisCompletedRows fields;
// tetris fiches + pawn promotion). v:10 and below silently discarded.
// Phase 23 patch: bumped to v:12 (tetris shapes now pivot-relative; old top-left
// anchored shapes are incompatible with pivot-based rotation). v:11 discarded.
// The old v1 localStorage key is intentionally NOT touched on load —
// renaming the key ensures we never even read v1's payload.

import type { SaveSnapshot } from '../state/GameState';

export const SAVE_KEY = 'roguelette_save_v2';

export function saveRun(snap: SaveSnapshot): void {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(snap));
  } catch {
    // Storage full / disabled — silent no-op
  }
}

export function loadRun(): SaveSnapshot | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SaveSnapshot;
    if (parsed.v !== 13) {
      // Schema mismatch (includes any pre-v:13 payload that somehow lands at
      // the v2 key) → discard silently, return null (per locked policy).
      localStorage.removeItem(SAVE_KEY);
      return null;
    }
    return parsed;
  } catch {
    // Corrupted JSON → discard silently
    try {
      localStorage.removeItem(SAVE_KEY);
    } catch {
      /* noop */
    }
    return null;
  }
}

export function clearSave(): void {
  try {
    localStorage.removeItem(SAVE_KEY);
  } catch {
    /* noop */
  }
}
